import type { UserAvatarUser } from '#layers/access/types/user-avatar';
import type { MonospaceCollectionMeta, MonospaceProject, PrimitiveType, PrimitiveTypeName } from '#layers/data-model';
import type { MonospaceAuditLog, ResolvedMonospaceCollection, ResolvedMonospaceDataSource, ResolvedMonospaceOrgRole, ResolvedMonospaceProject, ResolvedMonospaceServiceAccount } from '#layers/data-model/types/schema/resolved-schema';
import { formatTitle } from '@directus/format-title';
import { useI18n } from 'vue-i18n';
import { getTypename } from '#layers/data-model';
import { useTranslationUtils } from '#layers/i18n';

export interface FieldDisplayNameInput {
	apiName: string;
	meta?: { displayName?: string | null } | null;
}

export function useDisplayNames() {
	const { translateString } = useTranslationUtils();
	const { te, t } = useI18n();

	function getFieldDisplayName(field: FieldDisplayNameInput) {
		const raw = field.meta?.displayName ?? formatTitle(field.apiName);
		return translateString(raw);
	}

	function getPrimitiveFieldDisplayName(field: FieldDisplayNameInput) {
		return getFieldDisplayName(field);
	}

	function getSingleRelationFieldDisplayName(field: FieldDisplayNameInput) {
		return getFieldDisplayName(field);
	}

	function getCollectionDisplayName(
		collection: Pick<ResolvedMonospaceCollection, 'apiName'> & {
			meta?: Pick<MonospaceCollectionMeta, 'displayName'> | null;
		},
	) {
		const raw = collection.meta?.displayName ?? formatTitle(collection.apiName);
		return translateString(raw);
	}

	function getDataSourceDisplayName(source: Pick<ResolvedMonospaceDataSource, 'apiName'>) {
		return translateString(formatTitle(source.apiName));
	}

	function getPrimitiveTypeDisplayName(type: PrimitiveType | PrimitiveTypeName) {
		const typename = getTypename(type);
		const key = `types.${typename}`;
		return te(key) ? t(key) : typename;
	}

	function getUserDisplayName(user: UserAvatarUser): string {
		return user?.fullName ?? user?.email ?? t('common.unknown_user');
	}

	function getServiceAccountDisplayName(serviceAccount: ResolvedMonospaceServiceAccount): string {
		return serviceAccount?.fullName ?? t('common.unknown_service_account');
	}

	function getUserOrServiceAccountDisplayName(
		entity: UserAvatarUser | ResolvedMonospaceServiceAccount,
	): string {
		return 'email' in entity && entity.email !== undefined
			? getUserDisplayName(entity)
			: getServiceAccountDisplayName(entity as ResolvedMonospaceServiceAccount);
	}

	function getProjectDisplayName(
		project: Pick<MonospaceProject, 'displayName' | 'apiName'> | null | undefined,
	): string {
		if (project?.displayName) return project.displayName;
		if (project?.apiName) return formatTitle(project.apiName);
		return t('common.unknown_project');
	}

	function getAuditLogProjectLabel(
		auditLog: Pick<MonospaceAuditLog, 'payload' | 'projectId'>,
		projects: ResolvedMonospaceProject[] | null | undefined,
	): { label: string; isDeleted: boolean } {
		const payload = auditLog.payload as { display_name?: string; project_name?: string } | null;
		const payloadProjectName = payload?.display_name ?? payload?.project_name;

		if (payloadProjectName) return { label: payloadProjectName, isDeleted: false };
		if (!auditLog.projectId) return { label: t('organization.dashboard.settings.access.audit_logs.table.system'), isDeleted: false };

		const project = projects?.find((item) => item.id === auditLog.projectId);
		if (!project) return { label: t('organization.dashboard.settings.access.audit_logs.table.deleted_project'), isDeleted: true };

		return { label: getProjectDisplayName(project), isDeleted: false };
	}

	function getOrgRoleDisplayName(role: ResolvedMonospaceOrgRole | null | undefined): string {
		return role?.name ?? t('common.unknown_role');
	}

	return {
		getPrimitiveFieldDisplayName,
		getSingleRelationFieldDisplayName,
		getCollectionDisplayName,
		getDataSourceDisplayName,
		getPrimitiveTypeDisplayName,
		getFieldDisplayName,
		getUserDisplayName,
		getServiceAccountDisplayName,
		getUserOrServiceAccountDisplayName,
		getProjectDisplayName,
		getAuditLogProjectLabel,
		getOrgRoleDisplayName,
	};
}
